/**
 * Data transformers for converting between BE and internal formats
 *
 * BE uses UUIDs for node/edge IDs.
 * Internal format uses flow-position IDs (e.g., "1-1", "1-2", "2-1")
 * for better readability in Blueprint export/import.
 */

import { remapReferenceTagsDeep } from "components/CC/utils/remapReferenceTags";

import { DiagramBEData, DiagramState, SaveDiagramRequest } from "./types";
import { transformNodesToFlowPositionIds } from "../utils/flowPositionIds";

/**
 * Transform BE response to internal diagram state.
 * Converts UUID-based IDs to flow-position IDs (e.g., "1-1", "1-2").
 * Original UUIDs are stored in node.data.beId for later sync.
 */
export const transformToInternal = (beData: DiagramBEData): DiagramState => {
    const nodes = beData.nodes || [];
    const edges = beData.edges || [];

    // Transform UUIDs to flow-position IDs
    const { nodes: transformedNodes, edges: transformedEdges } = transformNodesToFlowPositionIds(nodes, edges);

    return {
        nodes: transformedNodes,
        edges: transformedEdges,
        notes: beData.notes || "",
    };
};

/**
 * Transform internal state to BE request format.
 * Converts flow-position IDs back to UUIDs (using beId if available,
 * or generating new UUIDs for new nodes).
 */
export const transformToExternal = (state: DiagramState): SaveDiagramRequest => {
    // Build beId lookup map
    const nodeBeIdMap = new Map<string, string>();
    state.nodes.forEach((node) => {
        const beId = (node.data as Record<string, unknown>)?.beId as string | undefined;
        nodeBeIdMap.set(node.id, beId || crypto.randomUUID());
    });

    return {
        // Previous-step reference tags (e.g. "{2-3.field}") are written against flow-position
        // IDs while editing; rewrite them to the UUIDs above so they still resolve once sent to
        // BE, whose own temp_id -> real-id resolution only matches the IDs it was given.
        nodes: state.nodes.map((node) => ({
            ...node,
            id: nodeBeIdMap.get(node.id) || node.id,
            data: remapReferenceTagsDeep(node.data, nodeBeIdMap) as typeof node.data,
        })),
        edges: state.edges.map((edge) => ({
            ...edge,
            id: `edge-${nodeBeIdMap.get(edge.source) || edge.source}-${nodeBeIdMap.get(edge.target) || edge.target}`,
            source: nodeBeIdMap.get(edge.source) || edge.source,
            target: nodeBeIdMap.get(edge.target) || edge.target,
            data: remapReferenceTagsDeep(edge.data, nodeBeIdMap) as typeof edge.data,
        })),
        notes: state.notes,
    };
};
